/**
 * Building Service
 * Building (bina/tesis) işlemleri için API servisi
 */

import { apiClient } from '../client/apiClient';
import { ENDPOINTS } from '../config/endpoints';
import {
  // Request types (oluşturulacak)
  CreateRequest,
  BuildingCreateRequest,
  UpdateRequest,
  BuildingUpdateRequest,
  BuildingListRequest,
  BuildingUnitsRequest,
  BuildingStatisticsRequest,
  
  // Response types (oluşturulacak)
  BuildingResponse,
  BuildingStatisticsResponse,
  BuildingUnitResponse,
  BuildingHealthCheckResponse,
  
  // Common types
  ID,
  ApiResponse,
  PaginatedResponse,
  BaseQueryParams,
  ExportFormat
} from '../types';

/**
 * Building service interface
 */
export interface IBuildingService {
  // CRUD Operations
  getBuildings(params?: BuildingListRequest): Promise<PaginatedResponse<BuildingResponse>>;
  getBuildingById(id: ID): Promise<ApiResponse<BuildingResponse>>;
  createBuilding(data: BuildingCreateRequest): Promise<ApiResponse<BuildingResponse>>;
  updateBuilding(id: ID, data: BuildingUpdateRequest): Promise<ApiResponse<BuildingResponse>>;
  deleteBuilding(id: ID): Promise<ApiResponse<void>>;
  
  // Building Units
  getBuildingUnits(buildingId: ID, params?: BuildingUnitsRequest): Promise<PaginatedResponse<BuildingUnitResponse>>;
  addUnitToBuilding(buildingId: ID, unitId: ID): Promise<ApiResponse<void>>;
  removeUnitFromBuilding(buildingId: ID, unitId: ID): Promise<ApiResponse<void>>;
  getBuildingUnitById(buildingId: ID, unitId: ID): Promise<ApiResponse<any>>;
  
  // Building Statistics & Analytics
  getBuildingStatistics(buildingId: ID, params?: BuildingStatisticsRequest): Promise<ApiResponse<BuildingStatisticsResponse>>;
  getBuildingHealthCheck(buildingId: ID): Promise<ApiResponse<BuildingHealthCheckResponse>>;
  
  // Building Operations
  exportBuildingData(buildingId: ID, format: ExportFormat, filters?: any): Promise<Blob>;
  importBuildingData(buildingId: ID, file: File): Promise<ApiResponse<any>>;
  
  // Building Status
  activateBuilding(id: ID): Promise<ApiResponse<BuildingResponse>>;
  deactivateBuilding(id: ID): Promise<ApiResponse<BuildingResponse>>;
  closeBuilding(id: ID): Promise<ApiResponse<BuildingResponse>>;
  reopenBuilding(id: ID): Promise<ApiResponse<BuildingResponse>>;
  
  // Utility Methods
  searchBuildings(query: string, params?: BaseQueryParams): Promise<PaginatedResponse<BuildingResponse>>;
  getBuildingsByLocation(lat: number, lng: number, radius: number): Promise<PaginatedResponse<BuildingResponse>>;
  validateBuildingCode(code: string): Promise<ApiResponse<{ valid: boolean; message?: string }>>;
}

/**
 * Building Service Implementation
 */
class BuildingService implements IBuildingService {
  // ==================== CRUD OPERATIONS ====================
  
  async getBuildings(params?: BuildingListRequest): Promise<PaginatedResponse<BuildingResponse>> {
    return apiClient.getList<BuildingResponse>(ENDPOINTS.BUILDINGS.LIST, params, {
      cacheKey: `buildings_${JSON.stringify(params || {})}`,
      cacheTTL: 300000 // 5 dakika cache
    });
  }
  
  async getBuildingById(id: ID): Promise<ApiResponse<BuildingResponse>> {
    return apiClient.get<BuildingResponse>(ENDPOINTS.BUILDINGS.DETAIL(id), id, {
      cacheKey: `building_${id}`,
      cacheTTL: 300000 // 5 dakika cache
    });
  }
  
  async createBuilding(data: BuildingCreateRequest): Promise<ApiResponse<BuildingResponse>> {
    // Cache'i temizle
    apiClient.cancelRequest('building_cache');
    const createRequest: CreateRequest<BuildingResponse> = {
    data: data as Omit<BuildingResponse, 'id' | 'createdAt' | 'updatedAt'>
  };
    return apiClient.create<BuildingResponse>(ENDPOINTS.BUILDINGS.CREATE, createRequest);
  }
  
  async updateBuilding(id: ID, data: BuildingUpdateRequest): Promise<ApiResponse<BuildingResponse>> {
    // Cache'i temizle
    apiClient.cancelRequest('building_cache');
    apiClient.clearCache(`building_${id}`);
    
    const updateRequest: UpdateRequest<BuildingResponse> = {
        data: data as Omit<BuildingResponse, 'id' | 'createdAt' | 'updatedAt'>,
        id: id
    };
    return apiClient.update<BuildingResponse>(ENDPOINTS.BUILDINGS.BASE, updateRequest);
  }
  
  async deleteBuilding(id: ID): Promise<ApiResponse<void>> {
    // Cache'i temizle
    apiClient.cancelRequest('building_cache');
    apiClient.clearCache(`building_${id}`);
    
    return apiClient.delete(ENDPOINTS.BUILDINGS.BASE, id);
  }
  
  // ==================== BUILDING UNITS ====================
  
  async getBuildingUnits(
    buildingId: ID, 
    params?: BuildingUnitsRequest
  ): Promise<PaginatedResponse<BuildingUnitResponse>> {
    const cacheKey = `building_units_${buildingId}_${JSON.stringify(params || {})}`;
    
    return apiClient.getList<BuildingUnitResponse>(
      ENDPOINTS.BUILDINGS.UNITS(buildingId),
      params,
      {
        cacheKey,
        cacheTTL: 300000 // 5 dakika cache
      }
    );
  }
  
  async addUnitToBuilding(buildingId: ID, unitId: ID): Promise<ApiResponse<void>> {
    // Cache'i temizle
    apiClient.clearCache(`building_units_${buildingId}`);
    apiClient.clearCache(`building_${buildingId}`);
    
    return apiClient.customPost<void>(
      `${ENDPOINTS.BUILDINGS.UNITS(buildingId)}/${unitId}`,
      {}
    );
  }
  
  async removeUnitFromBuilding(buildingId: ID, unitId: ID): Promise<ApiResponse<void>> {
    // Cache'i temizle
    apiClient.clearCache(`building_units_${buildingId}`);
    apiClient.clearCache(`building_${buildingId}`);
    
    return apiClient.customDelete<void>(
      `${ENDPOINTS.BUILDINGS.UNITS(buildingId)}/${unitId}`
    );
  }
  
  async getBuildingUnitById(buildingId: ID, unitId: ID): Promise<ApiResponse<any>> {
    return apiClient.customGet<any>(
      `${ENDPOINTS.BUILDINGS.UNITS(buildingId)}/${unitId}`,
      {
        cacheKey: `building_unit_${buildingId}_${unitId}`,
        cacheTTL: 300000 // 5 dakika cache
      }
    );
  }
  
  // ==================== BUILDING STATISTICS & ANALYTICS ====================
  
  async getBuildingStatistics(
    buildingId: ID, 
    params?: BuildingStatisticsRequest
  ): Promise<ApiResponse<BuildingStatisticsResponse>> {
    const cacheKey = `building_stats_${buildingId}_${JSON.stringify(params || {})}`;
    
    return apiClient.customGet<BuildingStatisticsResponse>(
      ENDPOINTS.BUILDINGS.STATISTICS(buildingId),
      {
        params,
        cacheKey,
        cacheTTL: 300000 // 5 dakika cache
      }
    );
  }
  
  async getBuildingHealthCheck(buildingId: ID): Promise<ApiResponse<BuildingHealthCheckResponse>> {
    return apiClient.customGet<BuildingHealthCheckResponse>(
      `${ENDPOINTS.BUILDINGS.BASE}/${buildingId}/health`,
      {
        cacheKey: `building_health_${buildingId}_${Date.now()}`,
        cacheTTL: 60000 // 1 dakika cache
      }
    );
  }
  
  // ==================== BUILDING OPERATIONS ====================
  
  async exportBuildingData(
    buildingId: ID, 
    format: ExportFormat, 
    filters?: any
  ): Promise<Blob> {
    const exportData = {
      format,
      filters,
      buildingId
    };
    
    return apiClient.exportData(
      ENDPOINTS.BUILDINGS.BASE,
      format,
      exportData
    );
  }
  
  async importBuildingData(buildingId: ID, file: File): Promise<ApiResponse<any>> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('buildingId', buildingId.toString());
    
    // Cache'i temizle
    apiClient.clearCache(`building_${buildingId}`);
    apiClient.clearCache(`building_units_${buildingId}`);
    
    return apiClient.uploadFile(
      `${ENDPOINTS.BUILDINGS.BASE}/${buildingId}/import`,
      file,
      { buildingId }
    );
  }
  
  // ==================== BUILDING STATUS ====================
  
  async activateBuilding(id: ID): Promise<ApiResponse<BuildingResponse>> {
    // Cache'i temizle
    apiClient.clearCache(`building_${id}`);
    
    return apiClient.customPatch<BuildingResponse>(
      `${ENDPOINTS.BUILDINGS.BASE}/${id}/activate`,
      {}
    );
  }
  
  async deactivateBuilding(id: ID): Promise<ApiResponse<BuildingResponse>> {
    // Cache'i temizle
    apiClient.clearCache(`building_${id}`);
    
    return apiClient.customPatch<BuildingResponse>(
      `${ENDPOINTS.BUILDINGS.BASE}/${id}/deactivate`,
      {}
    );
  }
  
  async closeBuilding(id: ID): Promise<ApiResponse<BuildingResponse>> {
    // Cache'i temizle
    apiClient.clearCache(`building_${id}`);
    
    return apiClient.customPatch<BuildingResponse>(
      `${ENDPOINTS.BUILDINGS.BASE}/${id}/close`,
      {}
    );
  }
  
  async reopenBuilding(id: ID): Promise<ApiResponse<BuildingResponse>> {
    // Cache'i temizle
    apiClient.clearCache(`building_${id}`);
    
    return apiClient.customPatch<BuildingResponse>(
      `${ENDPOINTS.BUILDINGS.BASE}/${id}/reopen`,
      {}
    );
  }
  
  // ==================== UTILITY METHODS ====================
  
  async searchBuildings(
    query: string, 
    params?: BaseQueryParams
  ): Promise<PaginatedResponse<BuildingResponse>> {
    const searchParams = {
      ...params,
      search: query
    };
    
    return apiClient.getList<BuildingResponse>(
      `${ENDPOINTS.BUILDINGS.BASE}/search`,
      searchParams,
      {
        cacheKey: `building_search_${query}_${JSON.stringify(params || {})}`,
        cacheTTL: 300000 // 5 dakika cache
      }
    );
  }
  
  async getBuildingsByLocation(
    lat: number, 
    lng: number, 
    radius: number
  ): Promise<PaginatedResponse<BuildingResponse>> {
    const params = {
      lat,
      lng,
      radius,
      unit: 'km' as const
    };
    
    return apiClient.getList<BuildingResponse>(
      `${ENDPOINTS.BUILDINGS.BASE}/nearby`,
      params,
      {
        cacheKey: `buildings_nearby_${lat}_${lng}_${radius}`,
        cacheTTL: 300000 // 5 dakika cache
      }
    );
  }
  
  async validateBuildingCode(code: string): Promise<ApiResponse<{ valid: boolean; message?: string }>> {
    return apiClient.customGet<{ valid: boolean; message?: string }>(
      `${ENDPOINTS.BUILDINGS.BASE}/validate-code`,
      {
        params: { code },
        cacheKey: `building_code_validate_${code}`,
        cacheTTL: 600000 // 10 dakika cache
      }
    );
  }
  
  // ==================== ADDITIONAL METHODS ====================
  
  /**
   * Get buildings by tenant
   */
  async getBuildingsByTenant(tenantId: ID, params?: BaseQueryParams): Promise<PaginatedResponse<BuildingResponse>> {
    return apiClient.getList<BuildingResponse>(
      `${ENDPOINTS.BUILDINGS.BASE}/tenant/${tenantId}`,
      params,
      {
        cacheKey: `buildings_tenant_${tenantId}_${JSON.stringify(params || {})}`,
        cacheTTL: 300000 // 5 dakika cache
      }
    );
  }
  
  /**
   * Get building types
   */
  async getBuildingTypes(): Promise<ApiResponse<Array<{ id: string; name: string; description?: string }>>> {
    return apiClient.customGet<Array<{ id: string; name: string; description?: string }>>(
      `${ENDPOINTS.BUILDINGS.BASE}/types`,
      {
        cacheKey: 'building_types',
        cacheTTL: 3600000 // 1 saat cache
      }
    );
  }
  
  /**
   * Get building zones/floors
   */
  async getBuildingZones(buildingId: ID): Promise<ApiResponse<Array<any>>> {
    return apiClient.customGet<Array<any>>(
      `${ENDPOINTS.BUILDINGS.BASE}/${buildingId}/zones`,
      {
        cacheKey: `building_zones_${buildingId}`,
        cacheTTL: 300000 // 5 dakika cache
      }
    );
  }
  
  /**
   * Get building equipment
   */
  async getBuildingEquipment(buildingId: ID, params?: BaseQueryParams): Promise<PaginatedResponse<any>> {
    return apiClient.getList<any>(
      `${ENDPOINTS.BUILDINGS.BASE}/${buildingId}/equipment`,
      params,
      {
        cacheKey: `building_equipment_${buildingId}_${JSON.stringify(params || {})}`,
        cacheTTL: 300000 // 5 dakika cache
      }
    );
  }
  
  /**
   * Get building maintenance schedule
   */
  async getBuildingMaintenanceSchedule(buildingId: ID): Promise<ApiResponse<any>> {
    return apiClient.customGet<any>(
      `${ENDPOINTS.BUILDINGS.BASE}/${buildingId}/maintenance-schedule`,
      {
        cacheKey: `building_maintenance_schedule_${buildingId}`,
        cacheTTL: 300000 // 5 dakika cache
      }
    );
  }
  
  /**
   * Get building energy consumption
   */
  async getBuildingEnergyConsumption(buildingId: ID, period: string): Promise<ApiResponse<any>> {
    return apiClient.customGet<any>(
      `${ENDPOINTS.BUILDINGS.BASE}/${buildingId}/energy`,
      {
        params: { period },
        cacheKey: `building_energy_${buildingId}_${period}`,
        cacheTTL: 300000 // 5 dakika cache
      }
    );
  }
  
  /**
   * Get building occupancy
   */
  async getBuildingOccupancy(buildingId: ID): Promise<ApiResponse<{ current: number; capacity: number; percentage: number }>> {
    return apiClient.customGet<{ current: number; capacity: number; percentage: number }>(
      `${ENDPOINTS.BUILDINGS.BASE}/${buildingId}/occupancy`,
      {
        cacheKey: `building_occupancy_${buildingId}_${Date.now()}`,
        cacheTTL: 60000 // 1 dakika cache (real-time data)
      }
    );
  }
  
  /**
   * Get building safety status
   */
  async getBuildingSafetyStatus(buildingId: ID): Promise<ApiResponse<any>> {
    return apiClient.customGet<any>(
      `${ENDPOINTS.BUILDINGS.BASE}/${buildingId}/safety`,
      {
        cacheKey: `building_safety_${buildingId}`,
        cacheTTL: 300000 // 5 dakika cache
      }
    );
  }
}

// Singleton instance
export const buildingService = new BuildingService();

// Default export
export default buildingService;

export { BuildingService };